import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const ResumeBuilder = () => {    
    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`AI Resume Builder – Create a Professional Resume Easily`}</title>
            <meta
                name="description"
                content="Build a professional resume quickly with Hunar Pool’s AI-powered resume builder and improve your chances of getting noticed by employers."
            />

            {/* Hero Section */}
            <div className="text-center mb-16">
                <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r from-[#0096C7]/20 to-[#4361EE]/20 border border-[#0096C7]/30 mb-6">
                <span className="text-[#0096C7] font-medium text-sm">
                    <i className="fas fa-hourglass-half mr-2" /> COMING SOON
                </span>
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                    <span className="gradient-text">Resume</span> Builder
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                    We're creating the smartest resume builder that uses AI to craft
                    professional, ATS-friendly resumes tailored to your target jobs and
                    optimized for success.
                </p>
                <div className="typing-animation max-w-2xl mx-auto text-lg text-[#0096C7] font-medium mb-10">
                    Generating the perfect resume for you...
                </div>                
            </div>
            {/* Resume Preview Visualization */}
            <div className="gradient-border rounded-2xl p-8 md:p-12 mb-16">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="lg:w-1/2">
                    <div className="section-bg rounded-xl p-8 h-[400px] relative overflow-hidden border border-gray-800">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#0096C7] via-[#4361EE] to-[#06D6A0]" />
                    {/* Resume Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                        <h2 className="text-2xl font-bold text-white">
                            ALEXANDRA JOHNSON
                        </h2>
                        <div className="w-16 h-1 bg-gradient-to-r from-[#0096C7] to-[#4361EE]" />
                        </div>
                        <p className="text-gray-300">
                        Senior Product Manager | San Francisco, CA
                        </p>
                    </div>
                    {/* Resume Content Placeholder */}
                    <div className="space-y-6">
                        <div>
                        <div className="flex items-center mb-2">
                            <div className="w-3 h-3 rounded-full bg-[#0096C7] mr-3" />
                            <h3 className="font-bold text-white">Professional Summary</h3>
                        </div>
                        <div className="h-2 bg-gray-700 rounded w-full mb-1" />
                        <div className="h-2 bg-gray-700 rounded w-3/4 mb-1" />
                        <div className="h-2 bg-gray-700 rounded w-5/6" />
                        </div>
                        <div>
                        <div className="flex items-center mb-2">
                            <div className="w-3 h-3 rounded-full bg-[#4361EE] mr-3" />
                            <h3 className="font-bold text-white">Work Experience</h3>
                        </div>
                        <div className="space-y-3 pl-6">
                            <div className="h-2 bg-gray-700 rounded w-full" />
                            <div className="h-2 bg-gray-700 rounded w-4/5" />
                            <div className="h-2 bg-gray-700 rounded w-2/3" />
                        </div>
                        </div>
                        <div>
                        <div className="flex items-center mb-2">
                            <div className="w-3 h-3 rounded-full bg-[#06D6A0] mr-3" />
                            <h3 className="font-bold text-white">Skills</h3>
                        </div>
                        <div className="flex flex-wrap gap-2 pl-6">
                            <div className="px-3 py-1 bg-gradient-to-r from-[#0096C7]/20 to-[#4361EE]/20 rounded-full text-xs">
                            Product Strategy
                            </div>
                            <div className="px-3 py-1 bg-gradient-to-r from-[#4361EE]/20 to-[#06D6A0]/20 rounded-full text-xs">
                            Agile Methodologies
                            </div>
                            <div className="px-3 py-1 bg-gradient-to-r from-[#06D6A0]/20 to-[#0096C7]/20 rounded-full text-xs">
                            Data Analysis
                            </div>
                        </div>
                        </div>
                    </div>
                    {/* AI Badge */}
                    <div className="absolute bottom-4 right-4 flex items-center px-3 py-1 bg-gradient-to-r from-[#0096C7]/20 to-[#4361EE]/20 rounded-full border border-[#0096C7]/30">
                        <i className="fas fa-robot text-xs text-[#0096C7] mr-1" />
                        <span className="text-xs font-medium">AI OPTIMIZED</span>
                    </div>
                    </div>
                </div>
                <div className="lg:w-1/2">
                    <h2 className="text-3xl font-bold mb-6">Intelligent Resume Creation</h2>
                    <p className="text-gray-300 mb-6 text-lg">
                    Our AI Resume Builder analyzes thousands of successful resumes in your
                    industry to create a document that highlights your strengths, uses
                    industry-appropriate keywords, and formats perfectly for both human
                    readers and Applicant Tracking Systems.
                    </p>
                    <div className="space-y-4">
                    <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#0096C7]/20 to-[#0096C7]/10 flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="fas fa-check text-[#0096C7]" />
                        </div>
                        <div>
                        <h4 className="font-bold mb-1">ATS Optimization</h4>
                        <p className="text-gray-400 text-sm">
                            Ensures your resume passes through automated screening systems
                        </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#4361EE]/20 to-[#4361EE]/10 flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="fas fa-check text-[#4361EE]" />
                        </div>
                        <div>
                        <h4 className="font-bold mb-1">Industry-Tailored Content</h4>
                        <p className="text-gray-400 text-sm">
                            Generates content that speaks the language of your target
                            industry
                        </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#06D6A0]/20 to-[#06D6A0]/10 flex items-center justify-center mr-4 flex-shrink-0">
                        <i className="fas fa-check text-[#06D6A0]" />
                        </div>
                        <div>
                        <h4 className="font-bold mb-1">Smart Formatting</h4>
                        <p className="text-gray-400 text-sm">
                            Automatically formats for readability and professional
                            appearance
                        </p>
                        </div>
                    </div>
                    </div>
                </div>
                </div>
            </div>
            {/* Features Grid */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-center mb-12">
                What Makes Our Resume Builder Different
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Feature 1 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0096C7]/20 to-[#0096C7]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-filter text-2xl text-[#0096C7]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">ATS-Friendly Design</h3>
                    <p className="text-gray-300 mb-4">
                    Every resume is optimized to pass through Applicant Tracking Systems
                    used by 99% of Fortune 500 companies.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 2 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4361EE]/20 to-[#4361EE]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-bullseye text-2xl text-[#4361EE]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Job-Specific Tailoring</h3>
                    <p className="text-gray-300 mb-4">
                    AI analyzes job descriptions to highlight the most relevant skills and
                    experiences for each application.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 3 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#06D6A0]/20 to-[#06D6A0]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-magic text-2xl text-[#06D6A0]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Smart Content Generation</h3>
                    <p className="text-gray-300 mb-4">
                    Transform bullet points into compelling achievements with quantifiable
                    results and action-oriented language.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 4 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0096C7]/20 to-[#4361EE]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-chart-line text-2xl text-[#4361EE]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Performance Analytics</h3>
                    <p className="text-gray-300 mb-4">
                    Track how your resume performs with real companies and get suggestions
                    for improvement.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 5 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4361EE]/20 to-[#06D6A0]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-palette text-2xl text-[#06D6A0]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Multiple Templates</h3>
                    <p className="text-gray-300 mb-4">
                    Choose from professionally designed templates optimized for different
                    industries and career levels.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 6 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#06D6A0]/20 to-[#0096C7]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-file-export text-2xl text-[#0096C7]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">One-Click Export</h3>
                    <p className="text-gray-300 mb-4">
                    Export to PDF, Word, or directly apply to jobs with your optimized
                    resume attached automatically.
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-800 rounded-full text-xs">
                    <i
                        className="fas fa-cog animate-spin mr-1"
                        style={{ animationDuration: "2s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                </div>
            </div>
            {/* How It Works */}
            <div className="section-bg rounded-2xl p-8 mb-16 border border-gray-800">
                <h2 className="text-3xl font-bold text-center mb-12">
                How the AI Resume Builder Works
                </h2>
                <div className="grid md:grid-cols-4 gap-6">
                {/* Step 1 */}
                <div className="text-center relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    1
                    </div>
                    <h3 className="text-lg font-bold mb-3">Input Your Experience</h3>
                    <p className="text-gray-400 text-sm">
                    Enter your work history, education, and skills in simple forms
                    </p>
                    <div className="absolute top-8 right-0 hidden md:block">
                    <i className="fas fa-arrow-right text-gray-600 text-xl" />
                    </div>
                </div>
                {/* Step 2 */}
                <div className="text-center relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#4361EE] to-[#06D6A0] flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    2
                    </div>
                    <h3 className="text-lg font-bold mb-3">AI Analysis</h3>
                    <p className="text-gray-400 text-sm">
                    Our AI analyzes and enhances your content with industry insights
                    </p>
                    <div className="absolute top-8 right-0 hidden md:block">
                    <i className="fas fa-arrow-right text-gray-600 text-xl" />
                    </div>
                </div>
                {/* Step 3 */}
                <div className="text-center relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#06D6A0] to-[#0096C7] flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    3
                    </div>
                    <h3 className="text-lg font-bold mb-3">Choose Template</h3>
                    <p className="text-gray-400 text-sm">
                    Select from ATS-friendly templates designed for your industry
                    </p>
                    <div className="absolute top-8 right-0 hidden md:block">
                    <i className="fas fa-arrow-right text-gray-600 text-xl" />
                    </div>
                </div>
                {/* Step 4 */}
                <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    4
                    </div>
                    <h3 className="text-lg font-bold mb-3">Download &amp; Apply</h3>
                    <p className="text-gray-400 text-sm">
                    Export your optimized resume and start applying with confidence
                    </p>
                </div>
                </div>
            </div>
            {/* Resume Stats & Benefits */}
            <div className="gradient-border rounded-2xl p-8 mb-16">
                <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-4">Why Resume Quality Matters</h2>
                <p className="text-gray-300 max-w-2xl mx-auto">
                    The average recruiter spends just{" "}
                    <span className="text-[#0096C7] font-bold">7.4 seconds</span> looking at
                    a resume. Make every second count.
                </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6">
                    <div className="text-3xl font-bold text-[#0096C7] mb-2">75%</div>
                    <p className="text-gray-300">
                    of resumes are rejected by ATS before human review
                    </p>
                </div>
                <div className="text-center p-6">
                    <div className="text-3xl font-bold text-[#4361EE] mb-2">3-5x</div>
                    <p className="text-gray-300">
                    more interviews with an ATS-optimized resume
                    </p>
                </div>
                <div className="text-center p-6">
                    <div className="text-3xl font-bold text-[#06D6A0] mb-2">40%</div>
                    <p className="text-gray-300">
                    increase in callback rates with quantified achievements
                    </p>
                </div>
                <div className="text-center p-6">
                    <div className="text-3xl font-bold text-[#0096C7] mb-2">90%</div>
                    <p className="text-gray-300">
                    of recruiters prefer one-page resumes for non-executive roles
                    </p>
                </div>
                </div>
            </div>
        </div>  
    );
};


export default ResumeBuilder;